<template>
  <div class="account-security q-pa-sm q-px-md">
    <h2 class="account-security__title">Account security</h2>
    <p class="account-security__subtitle">Update your password to keep your account secure</p>

    <div class="account-security__actions">
      <q-btn flat dense label="Reset password" :loading="isLoading" @click="handleResetRequest" class="account-security__reset-btn" no-caps />
    </div>

    <!-- Success message -->
    <q-banner v-if="successMessage" class="account-security__message account-security__success">
      <template v-slot:avatar>
        <q-icon name="check_circle" color="white" />
      </template>
      {{ successMessage }}
    </q-banner>

    <!-- Notification message -->
    <q-banner v-if="warningMessage" class="account-security__message account-security__notification">
      <template v-slot:avatar>
        <q-icon name="mail_outline" color="secondary" />
      </template>
      {{ warningMessage }}
    </q-banner>

    <!-- Error message -->
    <q-banner v-if="error" class="account-security__message account-security__error">
      <template v-slot:avatar>
        <q-icon name="error" color="white" />
      </template>
      {{ error }}
    </q-banner>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AuthApi } from '@api/index';

export default {
  name: 'AccountSecurity',

  data() {
    return {
      error: null,
      successMessage: null,
      warningMessage: null,
      isLoading: false
    };
  },

  computed: {
    ...mapGetters({
      user: 'user/getUser'
    })
  },

  methods: {
    /**
     * Handles the password reset request
     * Sends the reset request to the API and displays appropriate messages
     * @async
     */
    async handleResetRequest() {
      // Reset previous messages
      this.error = null;
      this.successMessage = null;
      this.warningMessage = null;

      if (!this.user || !this.user.email) {
        this.error = 'User email not found. Please refresh the page and try again.';
        return;
      }

      this.isLoading = true;

      try {
        // Call the API
        const response = await AuthApi.resetPassword(this.user.email);

        if (response && response.message) {
          this.warningMessage = response.message;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Reset password error:', error);

        // Handle different error types
        if (error.response) {
          // The request was made and the server responded with a status code that falls out of the range of 2xx
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Server error. Please try again later.';
          this.error = errorMessage;
        } else if (error.request) {
          // The request was made but no response was received
          this.error = 'No response from server. Please check your internet connection.';
        } else {
          // Something happened in setting up the request that triggered an Error
          this.error = 'An error occurred. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.account-security {
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: left;
  overflow: hidden;
  padding: 16px 24px;

  &__title {
    @include inter-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0 0 2px 0;
  }

  &__subtitle {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0 0 16px 0;
  }

  &__actions {
    margin-bottom: 16px;
  }

  &__reset-btn {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    background-color: var(--main-btn-color);
    color: var(--main-text-color);
    padding: 8px 16px;
    border-radius: 8px;
  }

  &__message {
    margin-top: 16px;
    border-radius: 4px;
    margin-bottom: 8px;
  }

  &__success {
    background-color: var(--positive);
    color: var(--secondary-bg-color);
  }

  &__notification {
    background-color: var(--main-bg-color);
    color: var(--main-text-color);
    border: 1px solid var(--border-outlined-btn-color);
    padding: 8px 16px;
  }

  &__error {
    background-color: var(--negative);
    color: var(--secondary-bg-color);
  }
}
</style>
